import { useState } from 'react'
import { DataTable, Chip, Avatar } from '../ds/index.js'
import { fmtDate, fmtMoney, tsToDate } from '../lib/format.js'

const FEE_STATUS_LABEL = { offered: 'Offered', confirmed: 'Confirmed', paid: 'Paid' }

export default function EngagementTableView({ engagements = [], stages = [], usersById = {}, onOpen }) {
  const [sort, setSort] = useState(null)
  const stagesById = Object.fromEntries(stages.map((s) => [s.id, s]))

  const onSort = (key) => {
    setSort((s) => (s && s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }))
  }

  // Each comparable value carries a `missing` flag so nulls sort last
  // regardless of sort direction (rather than flipping to the front on desc).
  const sortValue = (e, key) => {
    switch (key) {
      case 'title':
        return { v: (e.title || '').toLowerCase(), missing: !e.title }
      case 'org':
        return { v: (e.org || '').toLowerCase(), missing: !e.org }
      case 'stage': {
        const name = stagesById[e.stage]?.name || ''
        return { v: name.toLowerCase(), missing: !name }
      }
      case 'date': {
        const d = tsToDate(e.date)
        return { v: d ? d.getTime() : 0, missing: !d }
      }
      case 'fee':
        return { v: typeof e.fee === 'number' ? e.fee : 0, missing: typeof e.fee !== 'number' }
      case 'feeStatus':
        return { v: (e.feeStatus || '').toLowerCase(), missing: !e.feeStatus }
      case 'ownerUid': {
        const name = usersById[e.ownerUid]?.name || ''
        return { v: name.toLowerCase(), missing: !name }
      }
      default:
        return { v: '', missing: false }
    }
  }

  let rows = engagements
  if (sort && sort.key) {
    const dirMul = sort.dir === 'desc' ? -1 : 1
    rows = [...engagements].sort((a, b) => {
      const av = sortValue(a, sort.key)
      const bv = sortValue(b, sort.key)
      // Missing values always sort last, independent of direction.
      if (av.missing && !bv.missing) return 1
      if (!av.missing && bv.missing) return -1
      if (av.missing && bv.missing) return 0
      if (typeof av.v === 'string' && typeof bv.v === 'string') {
        return av.v.localeCompare(bv.v) * dirMul
      }
      if (av.v < bv.v) return -1 * dirMul
      if (av.v > bv.v) return 1 * dirMul
      return 0
    })
  }

  const columns = [
    {
      key: 'title',
      header: 'Title',
      priority: 1,
      sortable: true,
      render: (e) => e.title || '—',
    },
    {
      key: 'org',
      header: 'Org',
      priority: 2,
      sortable: true,
      render: (e) => e.org || <span style={{ color: 'var(--color-muted)' }}>—</span>,
    },
    {
      key: 'stage',
      header: 'Stage',
      priority: 2,
      sortable: true,
      render: (e) => {
        const stage = stagesById[e.stage]
        return stage ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: stage.dot, flexShrink: 0 }} />
            <span>{stage.name}</span>
          </span>
        ) : (
          <span style={{ color: 'var(--color-muted)' }}>—</span>
        )
      },
    },
    {
      key: 'date',
      header: 'Date',
      priority: 3,
      align: 'right',
      sortable: true,
      render: (e) => {
        const date = tsToDate(e.date)
        return (
          <span style={{ fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}>
            {date ? fmtDate(date) : <span style={{ color: 'var(--color-muted)' }}>—</span>}
          </span>
        )
      },
    },
    {
      key: 'fee',
      header: 'Fee',
      priority: 3,
      align: 'right',
      sortable: true,
      render: (e) => (
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontVariantNumeric: 'tabular-nums',
            color: typeof e.fee === 'number' ? 'var(--color-gold)' : 'var(--color-white)',
          }}
        >
          {typeof e.fee === 'number' ? fmtMoney(e.fee) : <span style={{ color: 'var(--color-muted)' }}>—</span>}
        </span>
      ),
    },
    {
      key: 'feeStatus',
      header: 'Fee status',
      priority: 3,
      sortable: true,
      render: (e) =>
        e.feeStatus ? (
          // Paid uses the "teal" chip variant (var(--pillar-city) / var(--color-teal),
          // #3DB89A) — colors.css already tokenizes this exact hex under that name,
          // so this replaces the old raw `#3DB89A` literal without inventing a new
          // token. Everything else keeps the existing gold variant.
          <Chip variant={e.feeStatus === 'paid' ? 'teal' : 'gold'}>{FEE_STATUS_LABEL[e.feeStatus] || e.feeStatus}</Chip>
        ) : (
          <span style={{ color: 'var(--color-muted)' }}>—</span>
        ),
    },
    {
      key: 'ownerUid',
      header: 'Owner',
      priority: 3,
      sortable: true,
      render: (e) => {
        const owner = usersById[e.ownerUid]
        return owner ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Avatar user={owner} size={20} />
            <span>{owner.name}</span>
          </div>
        ) : (
          <span style={{ color: 'var(--color-muted)' }}>—</span>
        )
      },
    },
  ]

  return (
    <DataTable
      columns={columns}
      rows={rows}
      rowKey={(e) => e.id}
      onRowClick={(e) => onOpen(e.id)}
      empty="No engagements yet."
      sort={sort}
      onSort={onSort}
    />
  )
}
